import React, { useLayoutEffect, useRef } from "react";
import styled from "styled-components";
import { MOBILE_GUTTER, TAP_TARGET } from "@/shared/theme/breakpoints";
import { pressedTint } from "@/shared/theme/pressed";

/*
 * Phone tab bar for the Manage Campaign editor, rendered through antd Tabs' `renderTabBar`, so
 * antd still owns the panes and the active key. antd's own bar scrolls with a transform and
 * resets native scroll on focus, which leaves off-screen tabs unreachable by touch; this rail
 * scrolls natively instead.
 */

export interface ManageCampaignTabRailItem {
	key: string;
	label: React.ReactNode;
}

interface Props {
	items: ManageCampaignTabRailItem[];
	activeKey: string;
	onChange: (key: string) => void;
}

const Rail = styled.div`
	display: flex;
	gap: 20px;
	padding: 0 ${MOBILE_GUTTER}px;
	border-bottom: 1px solid #ececf3;
	overflow-x: auto;
	scrollbar-width: none;
	overscroll-behavior-x: contain;

	&::-webkit-scrollbar {
		display: none;
	}
`;

const Tab = styled.button<{ $active: boolean }>`
	position: relative;
	flex-shrink: 0;
	min-height: ${TAP_TARGET}px;
	padding: 0 2px;
	border: none;
	background: transparent;
	color: ${({ $active }) => ($active ? "var(--color-pink, #f72586)" : "#454545")};
	font-family: inherit;
	font-size: 14px;
	font-weight: ${({ $active }) => ($active ? 600 : 500)};
	white-space: nowrap;
	cursor: pointer;

	&::after {
		content: "";
		position: absolute;
		left: 0;
		right: 0;
		bottom: 0;
		height: 3px;
		border-radius: 3px 3px 0 0;
		background: ${({ $active }) => ($active ? "var(--color-pink, #f72586)" : "transparent")};
	}

	&:focus-visible {
		outline: 2px solid #3a0ca3;
		outline-offset: -2px;
	}

	${pressedTint}
`;

export const ManageCampaignTabRail: React.FC<Props> = ({ items, activeKey, onChange }) => {
	const railRef = useRef<HTMLDivElement>(null);

	// Bring the active tab into view (deep links, arrow keys) without scrolling the page.
	useLayoutEffect(() => {
		const rail = railRef.current;
		const tab = rail?.querySelector<HTMLElement>('[aria-selected="true"]');
		if (!rail || !tab) return;
		const left = tab.offsetLeft - MOBILE_GUTTER;
		const right = tab.offsetLeft + tab.offsetWidth + MOBILE_GUTTER - rail.clientWidth;
		if (rail.scrollLeft > left) rail.scrollLeft = left;
		else if (rail.scrollLeft < right) rail.scrollLeft = right;
	}, [activeKey]);

	const onKeyDown = (event: React.KeyboardEvent) => {
		if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
		event.preventDefault();
		const index = items.findIndex((item) => item.key === activeKey);
		const next = items[(index + (event.key === "ArrowRight" ? 1 : -1) + items.length) % items.length];
		onChange(next.key);
		railRef.current?.querySelector<HTMLElement>(`[data-key="${next.key}"]`)?.focus();
	};

	return (
		<Rail ref={railRef} role="tablist" onKeyDown={onKeyDown}>
			{items.map((item) => {
				const active = item.key === activeKey;
				return (
					<Tab
						key={item.key}
						type="button"
						role="tab"
						data-key={item.key}
						aria-selected={active}
						tabIndex={active ? 0 : -1}
						$active={active}
						onClick={() => onChange(item.key)}
					>
						{item.label}
					</Tab>
				);
			})}
		</Rail>
	);
};
